import { ReactNode } from "react";

// Shared building blocks for legal pages (Privacy Policy, Terms of Service, …).
// Styled to the SkillPatch design system so every legal document reads as one set.

export const CONTACT_EMAIL = "contact@latentforce.ai";

export function Mail({ children }: { children?: ReactNode }) {
  return (
    <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent underline-offset-2 transition hover:underline">
      {children ?? CONTACT_EMAIL}
    </a>
  );
}

export function LegalHeader({
  title,
  accent,
  effective,
  children,
}: {
  title: string;
  accent: string;
  effective: string;
  children: ReactNode;
}) {
  return (
    <header>
      <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary">Legal</p>
      <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-[3.25rem]">
        {title} <span className="text-primary">{accent}</span>
      </h1>
      <p className="mt-5 text-xl leading-relaxed text-text/60">{children}</p>
      <div className="mt-7 flex items-center gap-3 text-xs text-text/45">
        <span className="h-[3px] w-11 rounded-full bg-gradient-to-r from-accent to-primary" />
        <span className="font-mono uppercase tracking-wide">Effective {effective}</span>
      </div>
    </header>
  );
}

export function Section({ title, children }: { number?: string; title: string; children: ReactNode }) {
  return (
    <section className="mt-10 scroll-mt-24">
      <h2 className="font-display text-[1.4rem] font-semibold tracking-tight sm:text-[1.7rem]">{title}</h2>
      <div className="mt-3 space-y-4">{children}</div>
    </section>
  );
}

export function P({ children }: { children: ReactNode }) {
  return <p className="text-[16.5px] leading-relaxed text-text/70">{children}</p>;
}

export function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="ml-1 space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3 text-[16.5px] leading-relaxed text-text/70">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function Table({ headers, rows }: { headers: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
      <table className="w-full border-collapse text-left text-[15px]">
        <thead>
          <tr className="bg-black/[0.03] dark:bg-white/[0.03]">
            {headers.map((h, i) => (
              <th key={i} className="px-4 py-3 font-semibold text-text/80">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className="border-t border-[var(--border)] align-top">
              {row.map((cell, c) => (
                <td key={c} className="px-4 py-3 text-text/70">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Callout({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-4">
      <p className="text-[16.5px] font-medium leading-relaxed text-text/85">{children}</p>
    </div>
  );
}

export function Contact({ lines }: { lines: ReactNode[] }) {
  return (
    <div className="rounded-xl border border-[var(--border)] bg-black/[0.02] dark:bg-white/[0.02] p-5">
      {lines.map((line, i) => (
        <p key={i} className="text-[16.5px] leading-relaxed text-text/70 [&:not(:first-child)]:mt-1.5">
          {line}
        </p>
      ))}
    </div>
  );
}
